import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldLabel,
  getFieldName,
  getFieldOptions,
  getFieldText,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { FormInput, FormRadioButton } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function SAFYCATS() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const clientAge = useMemo(() => {
    if (!clientData?.client?.dob || !getFieldValue(data.DateOfAssessment)) {
      return;
    }

    return moment(getFieldValue(data.DateOfAssessment)).diff(
      moment(getFieldText(clientData.client.dob)),
      "years"
    );
  }, [clientData, data]);

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const timingOptions = [
    { _id: "0", name: "0" },
    { _id: "1", name: "1" },
    { _id: "2", name: "2" },
    { _id: "3", name: "3" },
  ];

  const infoReady = useMemo(() => {
    if (
      !getFieldValue(data.DateOfAssessment) ||
      !getFieldValue(data.TypeOfAssessment)
    ) {
      return false;
    }

    if (!clientData?.client?.dob && !getFieldValue(data.TypeOfCATS)) {
      return false;
    }

    if (clientAge >= 7 && !getFieldValue(data.CompletedBy)) {
      return false;
    }

    return true;
  }, [data, clientAge, clientData]);

  const score = useMemo(() => {
    let total;
    const fields = [
      "UpsettingThoughts",
      "BadDreams",
      "EventNow",
      "UpsetReminder",
      "PhysicalReactions",
      "RepressedFeelings",
      "AvoidingReminders",
      "ForgetPart",
      "NegativeOutlook",
      "EventCausedWrong",
      "MoreNegativity",
      "LossOfInterest",
      "SocialWithdrawal",
      "LessPositivity",
      "Irritability",
      "RiskyBehavior",
      "HyperAware",
      "EasilyStartled",
      "LessConcentration",
      "SleepProblems",
    ];

    const fieldsWithValues = fields.filter(
      (f) => !isNaN(getFieldValue(data[f]))
    );
    if (fieldsWithValues.length === 0) {
      return total;
    }

    total = 0;
    fieldsWithValues.forEach((f) => {
      total += parseInt(getFieldValue(data[f]));
    });

    return total;
  }, [data]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateOfAssessment"
                    label={getFieldLabel(formConfig.DateOfAssessment)}
                    error={errors.DateOfAssessment}
                    value={getFieldValue(data.DateOfAssessment)}
                    onChange={handleChange}
                    identical={compare("DateOfAssessment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="TypeOfAssessment"
                    label={getFieldLabel(formConfig.TypeOfAssessment)}
                    error={errors.TypeOfAssessment}
                    value={getFieldValue(data.TypeOfAssessment)}
                    options={getFieldOptions(data.TypeOfAssessment)}
                    onChange={handleChange}
                    identical={compare("TypeOfAssessment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="row">
                  {!clientData?.client?.dob ? (
                    <div className="col">
                      <FormRadioButton
                        name="TypeOfCATS"
                        label={getFieldLabel(formConfig.TypeOfCATS)}
                        error={errors.TypeOfCATS}
                        value={getFieldValue(data.TypeOfCATS)}
                        options={getFieldOptions(data.TypeOfCATS)}
                        onChange={handleChange}
                        identical={compare("TypeOfCATS")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                  {clientAge >= 7 ? (
                    <div className="col">
                      <FormRadioButton
                        name="CompletedBy"
                        label={getFieldLabel(formConfig.CompletedBy)}
                        error={errors.CompletedBy}
                        value={getFieldValue(data.CompletedBy)}
                        options={getFieldOptions(data.CompletedBy)}
                        onChange={handleChange}
                        identical={compare("CompletedBy")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                  {clientAge < 7 ||
                  getFieldValue(data.CompletedBy) === "Caregiver" ||
                  getFieldValue(data.TypeOfCATS) === "Caregiver" ? (
                    <div className="col">
                      <FormInput
                        name="CaregiverName"
                        label={getFieldLabel(formConfig.CaregiverName)}
                        error={errors.CaregiverName}
                        value={getFieldValue(data.CaregiverName)}
                        onChange={handleChange}
                        identical={compare("CaregiverName")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              {infoReady ? (
                <>
                  {clientAge < 7 ||
                  getFieldValue(data.CompletedBy) === "Caregiver" ||
                  getFieldValue(data.TypeOfCATS) === "Caregiver" ? (
                    <p className="fw-bold">
                      Stressful or scary events happen to many children. Below
                      is a list of stressful and scary events that sometimes
                      happen. Mark YES if it happened to the child to the best
                      of your knowledge. Mark NO if it didn’t happen to the
                      child.
                    </p>
                  ) : null}
                  {getFieldValue(data.CompletedBy) === "Youth" ||
                  getFieldValue(data.TypeOfCATS) === "Youth" ? (
                    <p className="fw-bold">
                      Stressful or scary events happen to many children. Below
                      is a list of stressful and scary events that sometimes
                      happen. Mark YES if it happened to you. Mark NO if it
                      didn’t happen to you.
                    </p>
                  ) : null}
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.NaturalDisaster)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="NaturalDisaster"
                        error={errors.NaturalDisaster}
                        value={getFieldValue(data.NaturalDisaster)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("NaturalDisaster")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.Accident)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="Accident"
                        error={errors.Accident}
                        value={getFieldValue(data.Accident)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("Accident")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.ThreatenFamily)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="ThreatenFamily"
                        error={errors.ThreatenFamily}
                        value={getFieldValue(data.ThreatenFamily)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("ThreatenFamily")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.ThreatenCommunity)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="ThreatenCommunity"
                        error={errors.ThreatenCommunity}
                        value={getFieldValue(data.ThreatenCommunity)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("ThreatenCommunity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.Attacked)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="Attacked"
                        error={errors.Attacked}
                        value={getFieldValue(data.Attacked)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("Attacked")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.ThreatenFamilyWitness)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="ThreatenFamilyWitness"
                        error={errors.ThreatenFamilyWitness}
                        value={getFieldValue(data.ThreatenFamilyWitness)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("ThreatenFamilyWitness")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.ThreatenCommuntiyWitness)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="ThreatenCommuntiyWitness"
                        error={errors.ThreatenCommuntiyWitness}
                        value={getFieldValue(data.ThreatenCommuntiyWitness)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("ThreatenCommuntiyWitness")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.SexualAbuse)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="SexualAbuse"
                        error={errors.SexualAbuse}
                        value={getFieldValue(data.SexualAbuse)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("SexualAbuse")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.SexualAbuseOnline)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="SexualAbuseOnline"
                        error={errors.SexualAbuseOnline}
                        value={getFieldValue(data.SexualAbuseOnline)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("SexualAbuseOnline")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.BulliedInPerson)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="BulliedInPerson"
                        error={errors.BulliedInPerson}
                        value={getFieldValue(data.BulliedInPerson)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("BulliedInPerson")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.BulliedOnline)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="BulliedOnline"
                        error={errors.BulliedOnline}
                        value={getFieldValue(data.BulliedOnline)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("BulliedOnline")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.RecentDeath)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="RecentDeath"
                        error={errors.RecentDeath}
                        value={getFieldValue(data.RecentDeath)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("RecentDeath")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.MedicalProcedure)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="MedicalProcedure"
                        error={errors.MedicalProcedure}
                        value={getFieldValue(data.MedicalProcedure)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("MedicalProcedure")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.AffectedByWar)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="AffectedByWar"
                        error={errors.AffectedByWar}
                        value={getFieldValue(data.AffectedByWar)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("AffectedByWar")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row border-bottom border-dark">
                    <div className="col-8 py-2">
                      {getFieldLabel(formConfig.OtherEvent)}
                    </div>
                    <div className="col-4 py-2">
                      <FormRadioButton
                        name="OtherEvent"
                        error={errors.OtherEvent}
                        value={getFieldValue(data.OtherEvent)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("OtherEvent")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {getFieldValue(data.OtherEvent) === "Yes" ? (
                    <FormInput
                      name="OtherEventDetails"
                      error={errors.OtherEventDetails}
                      value={getFieldValue(data.OtherEventDetails)}
                      onChange={handleChange}
                      identical={compare("OtherEventDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  {clientAge < 7 ||
                  getFieldValue(data.TypeOfCATS) === "Child" ? (
                    <>
                      <p className="fw-bold mt-4">
                        The following questions are{" "}
                        <span className="border-bottom border-2 border-dark">
                          about all the scary or stressful events that happened
                          to the child.
                        </span>
                      </p>
                      <p className="fw-bold">
                        Mark 0, 1, 2 or 3 for how often the following things
                        have bothered the child in the last two weeks:
                      </p>
                      <div className="row text-center fw-bold mb-4">
                        <div className="col border-end border-2 border-dark">
                          0 Never
                        </div>
                        <div className="col border-end border-2 border-dark">
                          1 Once in a while
                        </div>
                        <div className="col border-end border-2 border-dark">
                          2 Half the time
                        </div>
                        <div className="col">3 Almost always</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.UpsettingThoughts)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="UpsettingThoughts"
                            error={errors.UpsettingThoughts}
                            value={getFieldValue(data.UpsettingThoughts)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("UpsettingThoughts")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.BadDreams)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="BadDreams"
                            error={errors.BadDreams}
                            value={getFieldValue(data.BadDreams)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("BadDreams")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.EventNow)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EventNow"
                            error={errors.EventNow}
                            value={getFieldValue(data.EventNow)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EventNow")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.UpsetReminder)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="UpsetReminder"
                            error={errors.UpsetReminder}
                            value={getFieldValue(data.UpsetReminder)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("UpsetReminder")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.PhysicalReactions)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PhysicalReactions"
                            error={errors.PhysicalReactions}
                            value={getFieldValue(data.PhysicalReactions)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("PhysicalReactions")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.RepressedFeelings)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="RepressedFeelings"
                            error={errors.RepressedFeelings}
                            value={getFieldValue(data.RepressedFeelings)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("RepressedFeelings")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.AvoidingReminders)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="AvoidingReminders"
                            error={errors.AvoidingReminders}
                            value={getFieldValue(data.AvoidingReminders)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("AvoidingReminders")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.MoreNegativity)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MoreNegativity"
                            error={errors.MoreNegativity}
                            value={getFieldValue(data.MoreNegativity)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("MoreNegativity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.LossOfInterest)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LossOfInterest"
                            error={errors.LossOfInterest}
                            value={getFieldValue(data.LossOfInterest)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LossOfInterest")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.SocialWithdrawal)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SocialWithdrawal"
                            error={errors.SocialWithdrawal}
                            value={getFieldValue(data.SocialWithdrawal)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("SocialWithdrawal")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.LessPositivity)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LessPositivity"
                            error={errors.LessPositivity}
                            value={getFieldValue(data.LessPositivity)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LessPositivity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.Irritability)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="Irritability"
                            error={errors.Irritability}
                            value={getFieldValue(data.Irritability)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("Irritability")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.HyperAware)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HyperAware"
                            error={errors.HyperAware}
                            value={getFieldValue(data.HyperAware)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("HyperAware")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.EasilyStartled)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EasilyStartled"
                            error={errors.EasilyStartled}
                            value={getFieldValue(data.EasilyStartled)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EasilyStartled")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.LessConcentration)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LessConcentration"
                            error={errors.LessConcentration}
                            value={getFieldValue(data.LessConcentration)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LessConcentration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {getFieldLabel(formConfig.SleepProblems)}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SleepProblems"
                            error={errors.SleepProblems}
                            value={getFieldValue(data.SleepProblems)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("SleepProblems")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                  {getFieldValue(data.CompletedBy) === "Youth" ||
                  getFieldValue(data.TypeOfCATS) === "Youth" ? (
                    <>
                      <p className="fw-bold mt-4">
                        The following questions are{" "}
                        <span className="border-bottom border-2 border-dark">
                          about all the scary or stressful events that happened
                          to you.
                        </span>
                      </p>
                      <p className="fw-bold">
                        Mark 0, 1, 2 or 3 for how often the following things
                        have bothered you in the last two weeks:
                      </p>
                      <div className="row text-center fw-bold mb-4">
                        <div className="col border-end border-2 border-dark">
                          0 Never
                        </div>
                        <div className="col border-end border-2 border-dark">
                          1 Once in a while
                        </div>
                        <div className="col border-end border-2 border-dark">
                          2 Half the time
                        </div>
                        <div className="col">3 Almost always</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.UpsettingThoughts["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="UpsettingThoughts"
                            error={errors.UpsettingThoughts}
                            value={getFieldValue(data.UpsettingThoughts)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("UpsettingThoughts")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.BadDreams["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="BadDreams"
                            error={errors.BadDreams}
                            value={getFieldValue(data.BadDreams)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("BadDreams")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.EventNow["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EventNow"
                            error={errors.EventNow}
                            value={getFieldValue(data.EventNow)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EventNow")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.UpsetReminder["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="UpsetReminder"
                            error={errors.UpsetReminder}
                            value={getFieldValue(data.UpsetReminder)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("UpsetReminder")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.PhysicalReactions["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PhysicalReactions"
                            error={errors.PhysicalReactions}
                            value={getFieldValue(data.PhysicalReactions)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("PhysicalReactions")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.RepressedFeelings["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="RepressedFeelings"
                            error={errors.RepressedFeelings}
                            value={getFieldValue(data.RepressedFeelings)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("RepressedFeelings")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.AvoidingReminders["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="AvoidingReminders"
                            error={errors.AvoidingReminders}
                            value={getFieldValue(data.AvoidingReminders)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("AvoidingReminders")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.ForgetPart["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="ForgetPart"
                            error={errors.ForgetPart}
                            value={getFieldValue(data.ForgetPart)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("ForgetPart")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.NegativeOutlook["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="NegativeOutlook"
                            error={errors.NegativeOutlook}
                            value={getFieldValue(data.NegativeOutlook)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("NegativeOutlook")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.EventCausedWrong["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EventCausedWrong"
                            error={errors.EventCausedWrong}
                            value={getFieldValue(data.EventCausedWrong)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EventCausedWrong")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.MoreNegativity["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MoreNegativity"
                            error={errors.MoreNegativity}
                            value={getFieldValue(data.MoreNegativity)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("MoreNegativity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.LossOfInterest["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LossOfInterest"
                            error={errors.LossOfInterest}
                            value={getFieldValue(data.LossOfInterest)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LossOfInterest")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.SocialWithdrawal["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SocialWithdrawal"
                            error={errors.SocialWithdrawal}
                            value={getFieldValue(data.SocialWithdrawal)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("SocialWithdrawal")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.LessPositivity["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LessPositivity"
                            error={errors.LessPositivity}
                            value={getFieldValue(data.LessPositivity)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LessPositivity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.Irritability["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="Irritability"
                            error={errors.Irritability}
                            value={getFieldValue(data.Irritability)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("Irritability")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.RiskyBehavior["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="RiskyBehavior"
                            error={errors.RiskyBehavior}
                            value={getFieldValue(data.RiskyBehavior)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("RiskyBehavior")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.HyperAware["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HyperAware"
                            error={errors.HyperAware}
                            value={getFieldValue(data.HyperAware)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("HyperAware")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.EasilyStartled["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EasilyStartled"
                            error={errors.EasilyStartled}
                            value={getFieldValue(data.EasilyStartled)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EasilyStartled")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.LessConcentration["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LessConcentration"
                            error={errors.LessConcentration}
                            value={getFieldValue(data.LessConcentration)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LessConcentration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.SleepProblems["@youthLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SleepProblems"
                            error={errors.SleepProblems}
                            value={getFieldValue(data.SleepProblems)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("SleepProblems")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                  {getFieldValue(data.CompletedBy) === "Caregiver" ||
                  getFieldValue(data.TypeOfCATS) === "Caregiver" ? (
                    <>
                      <p className="fw-bold mt-4">
                        The following questions are{" "}
                        <span className="border-bottom border-2 border-dark">
                          about all the scary or stressful events that happened
                          to the child.
                        </span>
                      </p>
                      <p className="fw-bold">
                        Mark 0, 1, 2 or 3 for how often the following things
                        have bothered the child in the last two weeks:
                      </p>
                      <div className="row text-center fw-bold mb-4">
                        <div className="col border-end border-2 border-dark">
                          0 Never
                        </div>
                        <div className="col border-end border-2 border-dark">
                          1 Once in a while
                        </div>
                        <div className="col border-end border-2 border-dark">
                          2 Half the time
                        </div>
                        <div className="col">3 Almost always</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.UpsettingThoughts["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="UpsettingThoughts"
                            error={errors.UpsettingThoughts}
                            value={getFieldValue(data.UpsettingThoughts)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("UpsettingThoughts")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.BadDreams["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="BadDreams"
                            error={errors.BadDreams}
                            value={getFieldValue(data.BadDreams)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("BadDreams")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.EventNow["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EventNow"
                            error={errors.EventNow}
                            value={getFieldValue(data.EventNow)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EventNow")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.UpsetReminder["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="UpsetReminder"
                            error={errors.UpsetReminder}
                            value={getFieldValue(data.UpsetReminder)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("UpsetReminder")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.PhysicalReactions["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PhysicalReactions"
                            error={errors.PhysicalReactions}
                            value={getFieldValue(data.PhysicalReactions)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("PhysicalReactions")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.RepressedFeelings["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="RepressedFeelings"
                            error={errors.RepressedFeelings}
                            value={getFieldValue(data.RepressedFeelings)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("RepressedFeelings")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.AvoidingReminders["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="AvoidingReminders"
                            error={errors.AvoidingReminders}
                            value={getFieldValue(data.AvoidingReminders)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("AvoidingReminders")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.ForgetPart["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="ForgetPart"
                            error={errors.ForgetPart}
                            value={getFieldValue(data.ForgetPart)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("ForgetPart")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.NegativeOutlook["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="NegativeOutlook"
                            error={errors.NegativeOutlook}
                            value={getFieldValue(data.NegativeOutlook)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("NegativeOutlook")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.EventCausedWrong["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EventCausedWrong"
                            error={errors.EventCausedWrong}
                            value={getFieldValue(data.EventCausedWrong)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EventCausedWrong")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.MoreNegativity["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MoreNegativity"
                            error={errors.MoreNegativity}
                            value={getFieldValue(data.MoreNegativity)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("MoreNegativity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.LossOfInterest["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LossOfInterest"
                            error={errors.LossOfInterest}
                            value={getFieldValue(data.LossOfInterest)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LossOfInterest")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.SocialWithdrawal["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SocialWithdrawal"
                            error={errors.SocialWithdrawal}
                            value={getFieldValue(data.SocialWithdrawal)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("SocialWithdrawal")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.LessPositivity["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LessPositivity"
                            error={errors.LessPositivity}
                            value={getFieldValue(data.LessPositivity)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LessPositivity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.Irritability["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="Irritability"
                            error={errors.Irritability}
                            value={getFieldValue(data.Irritability)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("Irritability")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.RiskyBehavior["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="RiskyBehavior"
                            error={errors.RiskyBehavior}
                            value={getFieldValue(data.RiskyBehavior)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("RiskyBehavior")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.HyperAware["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HyperAware"
                            error={errors.HyperAware}
                            value={getFieldValue(data.HyperAware)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("HyperAware")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.EasilyStartled["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EasilyStartled"
                            error={errors.EasilyStartled}
                            value={getFieldValue(data.EasilyStartled)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("EasilyStartled")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.LessConcentration["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LessConcentration"
                            error={errors.LessConcentration}
                            value={getFieldValue(data.LessConcentration)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("LessConcentration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-8">
                          {data.SleepProblems["@caregiverLabel"]}
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SleepProblems"
                            error={errors.SleepProblems}
                            value={getFieldValue(data.SleepProblems)}
                            options={timingOptions}
                            onChange={handleChange}
                            identical={compare("SleepProblems")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                  {score ? (
                    <div className="fw-bold mt-2">
                      This client has a score of: {score}
                    </div>
                  ) : null}
                  <div className="container fw-bold border border-dark mt-2">
                    {clientAge < 7 ||
                    getFieldValue(data.TypeOfCATS) === "Child" ? (
                      <div className="row border-bottom border-dark">
                        <div className="col bg-success border-end border-dark brightness-150">
                          CATS 3- 6 Years Score &lt;11
                        </div>
                        <div className="col bg-warning border-end border-dark brightness-125">
                          CATS 3-6 Years Score 12-15
                        </div>
                        <div className="col bg-danger brightness-150">
                          CATS 3-6 Years Score 15+
                        </div>
                      </div>
                    ) : null}
                    {clientAge >= 7 ||
                    getFieldValue(data.TypeOfCATS) === "Youth" ||
                    getFieldValue(data.TypeOfCATS) === "Caregiver" ? (
                      <div className="row border-bottom border-dark">
                        <div className="col bg-success border-end border-dark brightness-150">
                          CATS 7-17 Years Score &lt;15
                        </div>
                        <div className="col bg-warning border-end border-dark brightness-125">
                          CATS 7-17 Years Score 15-20
                        </div>
                        <div className="col bg-danger brightness-150">
                          CATS 7-17 Years Score 21+
                        </div>
                      </div>
                    ) : null}
                    <div className="row">
                      <div className="col bg-success border-end border-dark brightness-150">
                        Normal. Not clinically elevated.
                      </div>
                      <div className="col bg-warning border-end border-dark brightness-125">
                        Moderate trauma-related distress.
                      </div>
                      <div className="col bg-danger brightness-150">
                        Probable PTSD.
                      </div>
                    </div>
                  </div>
                  <p className="fw-bold mt-4">
                    Please mark YES or NO if the problems you marked interfered
                    with:
                  </p>
                  <div className="row row-cols-2">
                    <div className="col">
                      <FormRadioButton
                        name="GettingAlong"
                        label={getFieldLabel(formConfig.GettingAlong)}
                        error={errors.GettingAlong}
                        value={getFieldValue(data.GettingAlong)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("GettingAlong")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Hobbies"
                        label={getFieldLabel(formConfig.Hobbies)}
                        error={errors.Hobbies}
                        value={getFieldValue(data.Hobbies)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("Hobbies")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="SchoolWork"
                        label={getFieldLabel(formConfig.SchoolWork)}
                        error={errors.SchoolWork}
                        value={getFieldValue(data.SchoolWork)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("SchoolWork")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Relationships"
                        label={getFieldLabel(formConfig.Relationships)}
                        error={errors.Relationships}
                        value={getFieldValue(data.Relationships)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("Relationships")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Happiness"
                        label={getFieldLabel(formConfig.Happiness)}
                        error={errors.Happiness}
                        value={getFieldValue(data.Happiness)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        identical={compare("Happiness")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SAFYCATS;
